import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Search, LayoutGrid, List, X, Eye, AlertTriangle, Calendar, ArrowLeft, Activity, CheckCircle, BarChart3, GitBranch, ArrowRight } from 'lucide-react';
import { productionRecords, productionStages, productionPhaseConfig, getStageConfig, getMemberById, teamMembers, teamRoleConfig } from '../../data/productionData';
import { optionMeta, builderColors, getDaysInStage, buildersList } from './AdminProductionPage';
import { Badge } from '../../components/ui/Badge';
type ViewMode = 'kanban' | 'table';
type SummaryVariant = 'bar' | 'flow';
export function AdminBuilderProductionPage() {
  const {
    builderId
  } = useParams<{
    builderId: string;
  }>();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [summaryVariant, setSummaryVariant] = useState<SummaryVariant>('bar');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPhase, setFilterPhase] = useState('all');
  const [filterBlocker, setFilterBlocker] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [cardAssignees, setCardAssignees] = useState<Record<string, string>>({});
  const [openAssigneePopover, setOpenAssigneePopover] = useState<string | null>(null);
  const builder = buildersList.find(b => b.id === builderId);
  const joinedRecords = useMemo(() => {
    return productionRecords.filter(record => {
      const meta = optionMeta[record.optionId];
      return meta && meta.builderId === builderId;
    }).map(record => {
      const meta = optionMeta[record.optionId];
      const stageConfig = getStageConfig(record.productionStatus);
      const daysInStage = getDaysInStage(record.stageEnteredAt);
      return {
        ...record,
        ...meta,
        stageConfig,
        daysInStage
      };
    });
  }, [builderId]);
  const filteredRecords = useMemo(() => {
    return joinedRecords.filter(record => {
      if (searchQuery && !record.name.toLowerCase().includes(searchQuery.toLowerCase()) && !record.optionId.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (filterPhase !== 'all' && record.stageConfig.phase !== filterPhase) {
        return false;
      }
      if (filterBlocker === 'blocked' && record.blockers.length === 0) {
        return false;
      }
      if (filterBlocker === 'unblocked' && record.blockers.length > 0) {
        return false;
      }
      if (filterAssignee !== 'all') {
        const isAssigned = Object.values(record.assignments).includes(filterAssignee);
        if (!isAssigned) return false;
      }
      return true;
    });
  }, [joinedRecords, searchQuery, filterPhase, filterBlocker, filterAssignee]);
  const stats = useMemo(() => {
    const s = {
      totalInPipeline: 0,
      onboarding: 0,
      development: 0,
      qa: 0,
      launch: 0,
      blocked: 0,
      live: 0
    };
    joinedRecords.forEach(r => {
      if (r.productionStatus === 'live') {
        s.live++;
      } else if (r.productionStatus !== 'not-started') {
        s.totalInPipeline++;
        if (r.stageConfig.phase === 'onboarding') s.onboarding++;
        if (r.stageConfig.phase === 'development') s.development++;
        if (r.stageConfig.phase === 'qa') s.qa++;
        if (r.stageConfig.phase === 'launch') s.launch++;
      }
      if (r.blockers.length > 0) s.blocked++;
    });
    return s;
  }, [joinedRecords]);
  const clearFilters = () => {
    setSearchQuery('');
    setFilterPhase('all');
    setFilterBlocker('all');
    setFilterAssignee('all');
  };
  const hasActiveFilters = searchQuery || filterPhase !== 'all' || filterBlocker !== 'all' || filterAssignee !== 'all';
  if (!builder) {
    return <div className="p-12 text-center" data-id="element-1005">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1006">
          Builder not found
        </h2>
        <Button className="mt-4" onClick={() => navigate('/admin/production')} data-id="element-1007">
          Back to Production
        </Button>
      </div>;
  }
  const totalPipeline = stats.totalInPipeline || 1; // prevent division by zero
  const getWidth = (val: number) => `${val / totalPipeline * 100}%`;
  return <div className="space-y-6 max-w-[1600px] mx-auto" data-id="element-1008">
      {/* Header */}
      <div className="flex items-center justify-between mb-2" data-id="element-1009">
        <div className="flex items-center gap-4" data-id="element-1010">
          <Button variant="ghost" onClick={() => navigate('/admin/production')} className="text-slate-500 hover:text-slate-900 px-2" data-id="element-1011">
            <ArrowLeft className="h-5 w-5" data-id="element-1012" />
          </Button>
          <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold ${builderColors[builder.id] || 'bg-slate-300 text-white'}`} data-id="element-1013">
            {builder.name.charAt(0)}
          </div>
          <div data-id="element-1014">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-1015">
              {builder.name} Production
            </h1>
            <p className="text-slate-500 text-sm" data-id="element-1016">
              {joinedRecords.length} total options
            </p>
          </div>
        </div>

        {/* Variant Switcher */}
        <div className="flex items-center bg-slate-100 p-1 rounded-lg" data-id="element-1017">
          <button onClick={() => setSummaryVariant('bar')} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${summaryVariant === 'bar' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1018">
            <BarChart3 className="h-3.5 w-3.5" data-id="element-1019" /> Bar
          </button>

          <button onClick={() => setSummaryVariant('flow')} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${summaryVariant === 'flow' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1020">
            <GitBranch className="h-3.5 w-3.5" data-id="element-1021" /> Flow
          </button>
        </div>
      </div>

      {/* Summary Strip Variants */}
      {summaryVariant === 'bar' && <Card className="p-4 flex flex-col gap-3 shadow-sm" data-id="element-1022">
          <div className="flex items-center justify-between" data-id="element-1023">
            <div className="flex items-center gap-2" data-id="element-1024">
              <Activity className="h-4 w-4 text-blue-500" data-id="element-1025" />
              <span className="text-sm font-semibold text-slate-900" data-id="element-1026">
                {stats.totalInPipeline} In Pipeline
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-1027">
              {stats.blocked > 0 && <Badge variant="error" className="flex items-center gap-1 text-xs py-0.5" data-id="element-1028">
                  <AlertTriangle className="h-3 w-3" data-id="element-1029" />
                  {stats.blocked} Blocked
                </Badge>}
              <Badge variant="success" className="flex items-center gap-1 text-xs py-0.5" data-id="element-1030">
                <CheckCircle className="h-3 w-3" data-id="element-1031" />
                {stats.live} Live
              </Badge>
            </div>
          </div>
          <div className="h-4 flex rounded-full overflow-hidden w-full bg-slate-100" data-id="element-1032">
            {stats.onboarding > 0 && <div style={{
          width: getWidth(stats.onboarding)
        }} className="bg-slate-400 transition-all" title={`Onboarding: ${stats.onboarding}`} data-id="element-1033" />}
            {stats.development > 0 && <div style={{
          width: getWidth(stats.development)
        }} className="bg-blue-500 transition-all" title={`Development: ${stats.development}`} data-id="element-1034" />}
            {stats.qa > 0 && <div style={{
          width: getWidth(stats.qa)
        }} className="bg-violet-500 transition-all" title={`QA: ${stats.qa}`} data-id="element-1035" />}
            {stats.launch > 0 && <div style={{
          width: getWidth(stats.launch)
        }} className="bg-cyan-500 transition-all" title={`Launch: ${stats.launch}`} data-id="element-1036" />}
          </div>
          <div className="flex justify-between text-[10px] font-medium text-slate-500 px-1" data-id="element-1037">
            <span data-id="element-1038">Onboarding ({stats.onboarding})</span>
            <span data-id="element-1039">Development ({stats.development})</span>
            <span data-id="element-1040">QA ({stats.qa})</span>
            <span data-id="element-1041">Launch ({stats.launch})</span>
          </div>
        </Card>}

      {summaryVariant === 'flow' && <Card className="p-4 shadow-sm overflow-x-auto" data-id="element-1042">
          <div className="flex items-center min-w-max" data-id="element-1043">
            <div className="flex flex-col items-center gap-1 px-4 py-2 bg-slate-50 rounded-lg border border-slate-200" data-id="element-1044">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-1045">
                Onboarding
              </span>
              <span className="text-lg font-bold text-slate-700" data-id="element-1046">
                {stats.onboarding}
              </span>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-300 mx-2 flex-shrink-0" data-id="element-1047" />
            <div className="flex flex-col items-center gap-1 px-4 py-2 bg-blue-50 rounded-lg border border-blue-200" data-id="element-1048">
              <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider" data-id="element-1049">
                Development
              </span>
              <span className="text-lg font-bold text-blue-700" data-id="element-1050">
                {stats.development}
              </span>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-300 mx-2 flex-shrink-0" data-id="element-1051" />
            <div className="flex flex-col items-center gap-1 px-4 py-2 bg-violet-50 rounded-lg border border-violet-200" data-id="element-1052">
              <span className="text-[10px] font-bold text-violet-600 uppercase tracking-wider" data-id="element-1053">
                QA
              </span>
              <span className="text-lg font-bold text-violet-700" data-id="element-1054">
                {stats.qa}
              </span>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-300 mx-2 flex-shrink-0" data-id="element-1055" />
            <div className="flex flex-col items-center gap-1 px-4 py-2 bg-cyan-50 rounded-lg border border-cyan-200" data-id="element-1056">
              <span className="text-[10px] font-bold text-cyan-600 uppercase tracking-wider" data-id="element-1057">
                Launch
              </span>
              <span className="text-lg font-bold text-cyan-700" data-id="element-1058">
                {stats.launch}
              </span>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-300 mx-2 flex-shrink-0" data-id="element-1059" />
            <div className="flex flex-col items-center gap-1 px-4 py-2 bg-emerald-50 rounded-lg border border-emerald-200" data-id="element-1060">
              <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider" data-id="element-1061">
                Live
              </span>
              <span className="text-lg font-bold text-emerald-700" data-id="element-1062">
                {stats.live}
              </span>
            </div>

            <div className="ml-auto pl-6 border-l border-slate-200 flex items-center gap-4" data-id="element-1063">
              <div className="flex flex-col items-center gap-1" data-id="element-1064">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-1065">
                  In Pipeline
                </span>
                <span className="text-lg font-bold text-slate-900" data-id="element-1066">
                  {stats.totalInPipeline}
                </span>
              </div>
              {stats.blocked > 0 && <div className="flex flex-col items-center gap-1 px-3 py-1.5 bg-red-50 rounded-lg border border-red-200" data-id="element-1067">
                  <span className="text-[10px] font-bold text-red-600 uppercase tracking-wider flex items-center gap-1" data-id="element-1068">
                    <AlertTriangle className="h-3 w-3" data-id="element-1069" /> Blocked
                  </span>
                  <span className="text-lg font-bold text-red-700" data-id="element-1070">
                    {stats.blocked}
                  </span>
                </div>}
            </div>
          </div>
        </Card>}

      {/* Controls */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-id="element-1071">
        <Card className="p-2 flex-1 w-full" data-id="element-1072">
          <div className="flex flex-wrap items-center gap-3" data-id="element-1073">
            <div className="relative flex-1 min-w-[200px]" data-id="element-1074">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-1075" />
              <input type="text" placeholder="Search options..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors" data-id="element-1076" />
            </div>
            <select value={filterPhase} onChange={e => setFilterPhase(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors" data-id="element-1077">
              <option value="all" data-id="element-1078">All Phases</option>
              {Object.entries(productionPhaseConfig).map(([key, config]) => <option key={key} value={key} data-id="element-1079">
                  {config.label}
                </option>)}
            </select>
            <select value={filterBlocker} onChange={e => setFilterBlocker(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors" data-id="element-1080">
              <option value="all" data-id="element-1081">All Status</option>
              <option value="blocked" data-id="element-1082">Blocked Only</option>
              <option value="unblocked" data-id="element-1083">No Blockers</option>
            </select>
            <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors" data-id="element-1084">
              <option value="all" data-id="element-1085">All Assignees</option>
              {teamMembers.map(member => <option key={member.id} value={member.id} data-id="element-1086">
                  {member.name}
                </option>)}
            </select>
            {hasActiveFilters && <Button variant="ghost" onClick={clearFilters} leftIcon={<X className="h-4 w-4" data-id="element-1088" />} data-id="element-1087">
                Clear
              </Button>}
          </div>
        </Card>

        <div className="flex items-center bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm flex-shrink-0" data-id="element-1089">
          <button onClick={() => setViewMode('kanban')} className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors ${viewMode === 'kanban' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`} data-id="element-1090">
            <LayoutGrid className="h-4 w-4" data-id="element-1091" />
            Board
          </button>
          <button onClick={() => setViewMode('table')} className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-l border-slate-200 ${viewMode === 'table' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`} data-id="element-1092">
            <List className="h-4 w-4" data-id="element-1093" />
            Table
          </button>
        </div>
      </div>

      {/* Main Content */}
      {viewMode === 'kanban' ? <div className="flex gap-4 overflow-x-auto pb-6 min-h-[600px]" data-id="element-1094">
          {productionStages.map(stage => {
        const stageRecords = filteredRecords.filter(r => r.productionStatus === stage.key);
        const phaseConfig = productionPhaseConfig[stage.phase];
        const topBorderColor = phaseConfig.color.replace('text-', 'bg-');
        return <div key={stage.key} className="flex-shrink-0 w-[280px] flex flex-col bg-slate-50/50 rounded-xl border border-slate-200 overflow-hidden relative" data-id="element-1095">
                {/* Accent Top Border */}
                <div className={`absolute top-0 left-0 right-0 h-1 ${topBorderColor}`} data-id="element-1096" />

                {/* Column Header */}
                <div className="px-3 pt-3.5 pb-2.5 border-b border-slate-200 bg-white/50" data-id="element-1097">
                  <div className="flex items-center justify-between mb-0.5" data-id="element-1098">
                    <div className="flex items-center gap-2" data-id="element-1099">
                      <div className={`w-2 h-2 rounded-full ${stage.dotColor}`} data-id="element-1100" />
                      <span className={`text-sm font-semibold ${phaseConfig.color}`} data-id="element-1101">
                        {stage.shortLabel}
                      </span>
                    </div>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded" data-id="element-1102">
                      {stageRecords.length}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium" data-id="element-1103">
                    {phaseConfig.label}
                  </p>
                </div>

                {/* Column Content */}
                <div className="flex-1 p-2.5 space-y-2.5 overflow-y-auto" data-id="element-1104">
                  {stageRecords.length === 0 ? <div className="h-20 flex items-center justify-center border-2 border-dashed border-slate-200 rounded-lg" data-id="element-1105">
                      <p className="text-xs text-slate-400 font-medium" data-id="element-1106">
                        No options
                      </p>
                    </div> : stageRecords.map(record => {
              const pmId = cardAssignees[record.optionId] || record.assignments.pm;
              const pm = pmId ? getMemberById(pmId) : null;
              const isBlocked = record.blockers.length > 0;
              return <div key={record.optionId} onClick={() => navigate(`/admin/options/${record.optionId}`)} className={`bg-white p-3 rounded-lg border shadow-sm cursor-pointer hover:shadow-md transition-all group ${isBlocked ? 'border-red-300 ring-1 ring-red-100' : 'border-slate-200 hover:border-blue-300'}`} data-id="element-1107">
                          <div className="flex items-start justify-between gap-2 mb-3" data-id="element-1108">
                            <h4 className="text-sm font-semibold text-slate-900 group-hover:text-blue-700 transition-colors leading-tight" data-id="element-1109">
                              {record.name}
                            </h4>
                            <Eye className="h-3.5 w-3.5 text-slate-300 group-hover:text-blue-500 flex-shrink-0 mt-0.5 transition-colors" data-id="element-1110" />
                          </div>

                          <div className="flex items-center justify-between relative" data-id="element-1111">
                            <div className="flex items-center gap-2" data-id="element-1112">
                              {pm ? <div onClick={e => {
                      e.stopPropagation();
                      setOpenAssigneePopover(openAssigneePopover === record.optionId ? null : record.optionId);
                    }} className={`h-5 w-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-blue-400 transition-all ${pm.avatarColor}`} title={`Assignee: ${pm.name}`} data-id="element-1113">
                                  {pm.initials}
                                </div> : <div onClick={e => {
                      e.stopPropagation();
                      setOpenAssigneePopover(openAssigneePopover === record.optionId ? null : record.optionId);
                    }} className="h-5 w-5 rounded-full border border-dashed border-slate-300 flex items-center justify-center text-slate-400 hover:text-blue-500 hover:border-blue-500 cursor-pointer transition-colors" title="Assign owner" data-id="element-1114">
                                  <span className="text-[10px]" data-id="element-1115">+</span>
                                </div>}

                              {openAssigneePopover === record.optionId && <div className="absolute left-0 top-6 mt-1 w-56 bg-white rounded-lg shadow-lg border border-slate-200 z-50 py-1" onClick={e => e.stopPropagation()} data-id="element-1116">
                                  <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center" data-id="element-1117">
                                    <p className="text-xs font-medium text-slate-500" data-id="element-1118">
                                      Select Assignee
                                    </p>
                                    <button onClick={() => setOpenAssigneePopover(null)} className="text-slate-400 hover:text-slate-600" data-id="element-1119">
                                      <X className="h-3 w-3" data-id="element-1120" />
                                    </button>
                                  </div>
                                  <div className="max-h-64 overflow-y-auto py-1" data-id="element-1121">
                                    {Object.entries(teamRoleConfig).map(([roleKey, roleConfig]) => {
                          const roleMembers = teamMembers.filter(m => m.role === roleKey);
                          if (roleMembers.length === 0) return null;
                          return <div key={roleKey} className="mb-2 last:mb-0" data-id="element-1122">
                                            <div className="px-3 py-1" data-id="element-1123">
                                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-1124">
                                                {roleConfig.label}
                                              </span>
                                            </div>
                                            {roleMembers.map(member => <button key={member.id} onClick={() => {
                              setCardAssignees(prev => ({
                                ...prev,
                                [record.optionId]: member.id
                              }));
                              setOpenAssigneePopover(null);
                            }} className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50 transition-colors ${pmId === member.id ? 'bg-blue-50/50' : ''}`} data-id="element-1125">
                                                <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white ${member.avatarColor}`} data-id="element-1126">
                                                  {member.initials}
                                                </div>
                                                <span className={`text-xs ${pmId === member.id ? 'font-medium text-blue-700' : 'text-slate-700'}`} data-id="element-1127">
                                                  {member.name}
                                                </span>
                                                {pmId === member.id && <CheckCircle className="h-3 w-3 text-blue-600 ml-auto" data-id="element-1128" />}
                                              </button>)}
                                          </div>;
                        })}
                                  </div>
                                </div>}
                              {isBlocked && <div className="flex items-center gap-1 text-red-600" data-id="element-1129">
                                  <AlertTriangle className="h-3 w-3" data-id="element-1130" />
                                  <span className="text-[10px] font-semibold" data-id="element-1131">
                                    Blocked
                                  </span>
                                </div>}
                              {record.productionStatus === 'internal-qa' && record.qaLevel && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200" data-id="element-1132">
                                    Round {record.qaLevel}
                                  </span>}
                            </div>
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${record.daysInStage > 30 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-1133">
                              {record.daysInStage}d
                            </span>
                          </div>

                          {record.targetDate && <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-100" data-id="element-1134">
                              <Calendar className="h-3 w-3 text-slate-400" data-id="element-1135" />
                              <span className="text-[10px] text-slate-500" data-id="element-1136">
                                Target: {record.targetDate}
                              </span>
                            </div>}
                        </div>;
            })}
                </div>
              </div>;
      })}
        </div> /* Table View */ : <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-1137">
          <div className="overflow-x-auto" data-id="element-1138">
            <table className="w-full text-left" data-id="element-1139">
              <thead data-id="element-1140">
                <tr className="bg-slate-50 border-b border-slate-200" data-id="element-1141">
                  <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1142">
                    Option
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1143">
                    Stage
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1144">
                    Phase
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1145">
                    Assignee
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1146">
                    Days in Stage
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1147">
                    Blockers
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1148">
                    Target
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1149">
                {filteredRecords.length === 0 ? <tr data-id="element-1150">
                    <td colSpan={7} className="px-4 py-12 text-center text-slate-500" data-id="element-1151">
                      No options found matching your filters.
                    </td>
                  </tr> : filteredRecords.map(record => {
              const pmId = record.assignments.pm;
              const pm = pmId ? getMemberById(pmId) : null;
              const isBlocked = record.blockers.length > 0;
              const phaseConfig = productionPhaseConfig[record.stageConfig.phase];
              return <tr key={record.optionId} className={`hover:bg-slate-50 transition-colors cursor-pointer ${isBlocked ? 'bg-red-50/30' : ''}`} onClick={() => navigate(`/admin/options/${record.optionId}`)} data-id="element-1152">
                        <td className="px-4 py-3" data-id="element-1153">
                          <div data-id="element-1154">
                            <span className="font-medium text-slate-900 text-sm" data-id="element-1155">
                              {record.name}
                            </span>
                            <p className="text-xs text-slate-400 mt-0.5" data-id="element-1156">
                              {record.optionId} · {record.category}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3" data-id="element-1157">
                          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border ${record.stageConfig.bgColor} ${record.stageConfig.color} ${record.stageConfig.borderColor}`} data-id="element-1158">
                            <div className={`w-1.5 h-1.5 rounded-full ${record.stageConfig.dotColor}`} data-id="element-1159" />
                            {record.stageConfig.shortLabel}
                            {record.productionStatus === 'internal-qa' && record.qaLevel && <span className="ml-1 text-[10px] font-semibold px-1 py-0.5 rounded bg-violet-200/60 text-violet-800" data-id="element-1160">
                                  R{record.qaLevel}
                                </span>}
                          </div>
                        </td>
                        <td className="px-4 py-3" data-id="element-1161">
                          <span className={`text-xs font-medium px-2 py-1 rounded ${phaseConfig.bgColor} ${phaseConfig.color}`} data-id="element-1162">
                            {phaseConfig.label}
                          </span>
                        </td>
                        <td className="px-4 py-3" data-id="element-1163">
                          {pm ? <div className="flex items-center gap-2" data-id="element-1164">
                              <div className={`h-6 w-6 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${pm.avatarColor}`} data-id="element-1165">
                                {pm.initials}
                              </div>
                              <span className="text-sm text-slate-700" data-id="element-1166">
                                {pm.name}
                              </span>
                            </div> : <span className="text-xs text-slate-400" data-id="element-1167">—</span>}
                        </td>
                        <td className="px-4 py-3" data-id="element-1168">
                          <span className={`text-xs font-medium px-2 py-1 rounded ${record.daysInStage > 30 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-1169">
                            {record.daysInStage} days
                          </span>
                        </td>
                        <td className="px-4 py-3" data-id="element-1170">
                          {isBlocked ? <div className="flex items-center gap-1 text-red-600" data-id="element-1171">
                              <AlertTriangle className="h-3.5 w-3.5" data-id="element-1172" />
                              <span className="text-xs font-medium" data-id="element-1173">
                                {record.blockers.length} blocker
                                {record.blockers.length !== 1 ? 's' : ''}
                              </span>
                            </div> : <span className="text-xs text-slate-400" data-id="element-1174">None</span>}
                        </td>
                        <td className="px-4 py-3" data-id="element-1175">
                          <span className="text-xs text-slate-600" data-id="element-1176">
                            {record.targetDate || '—'}
                          </span>
                        </td>
                      </tr>;
            })}
              </tbody>
            </table>
          </div>
        </div>}
    </div>;
}